import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';

interface JobArchiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobNumber?: string;
  onConfirm: (archiveJob: boolean) => void;
  isLoading: boolean;
  error?: string | null;
}

/**
 * Dialog for confirming Sold → Assigned transition
 * Asks user whether to archive the associated job or keep it
 */
export function JobArchiveDialog({
  open,
  onOpenChange,
  jobNumber,
  onConfirm,
  isLoading,
  error,
}: JobArchiveDialogProps) {
  const [archiveChoice, setArchiveChoice] = useState<'archive' | 'keep'>('keep');

  const handleConfirm = () => {
    onConfirm(archiveChoice === 'archive');
  };

  const handleCancel = () => {
    setArchiveChoice('keep');
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setArchiveChoice('keep');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Revert to Assigned
          </DialogTitle>
          <DialogDescription>
            You are reverting this lead from Sold back to Assigned.
            {jobNumber && (
              <span className="block mt-2 font-medium">
                Job Number: <span className="text-foreground">{jobNumber}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-800 border border-red-200">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-base font-medium">
              What should happen to the associated job?
            </Label>
            <RadioGroup
              value={archiveChoice}
              onValueChange={(value: string) => setArchiveChoice(value as 'archive' | 'keep')}
              className="space-y-3"
              disabled={isLoading}
            >
              <div className="flex items-start space-x-3 p-3 rounded-md border hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="keep" id="keep" />
                <div className="space-y-1">
                  <Label htmlFor="keep" className="font-medium cursor-pointer">
                    Keep the job
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    The job will remain active and can be used if the lead is sold again.
                    No data will be lost.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 rounded-md border hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="archive" id="archive" />
                <div className="space-y-1">
                  <Label htmlFor="archive" className="font-medium cursor-pointer">
                    Archive the job
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    The job will be archived and hidden from default views.
                    It can still be accessed if needed.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="action" onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Reverting...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
